// components/ui/Card.tsx
// UI Phase 1 primitive: warm surface card. Soft radius + layered shadow from
// the token scale; hover lift is opt-in (interactive) because most cards
// are static containers.

import { forwardRef } from 'react';
import type { HTMLAttributes } from 'react';
import { cn } from './cn';

export interface CardProps extends HTMLAttributes<HTMLDivElement> {
  /** Adds hover lift + pointer affordance for clickable cards */
  interactive?: boolean;
  /** Removes the default padding for full-bleed content */
  flush?: boolean;
}

export const Card = forwardRef<HTMLDivElement, CardProps>(
  ({ interactive = false, flush = false, className, children, ...rest }, ref) => (
    <div
      ref={ref}
      className={cn(
        'bg-surface-card border border-line-soft rounded-card shadow-sm',
        !flush && 'p-4',
        interactive &&
          'transition-all duration-fast ease-out-soft cursor-pointer hover:shadow-md hover:-translate-y-px',
        className
      )}
      {...rest}
    >
      {children}
    </div>
  )
);

Card.displayName = 'Card';

export const CardTitle = ({ className, children, ...rest }: HTMLAttributes<HTMLHeadingElement>) => (
  <h3 className={cn('text-[15px] font-semibold text-ink', className)} {...rest}>
    {children}
  </h3>
);

export const CardDescription = ({ className, children, ...rest }: HTMLAttributes<HTMLParagraphElement>) => (
  <p className={cn('text-sm text-ink-2', className)} {...rest}>
    {children}
  </p>
);

export default Card;
